import Select from './Select';
import Tabs from './Tabs';
import RadioButtons from './RadioButtons';
import Checkbox from './Checkbox';
import { Box, Stack, Typography, useTheme } from '@mui/joy';
//import { useControls } from '../../providers/ControlProvider';
import {
	CONGRESSIONAL_TABS,
	DONOR_TABS,
	ELECTION_OPTIONS,
	FILTER_BOX,
	DONOR_FILTER_BOX,
	SEAT_OPTIONS,
	STATE_OPTIONS,
	TEN_STATE_OPTIONS,
	TEN_STATE_STATE_OPTIONS
} from '../../lib/const';
import { Section } from '../Display';
import { SxProps } from '@mui/joy/styles/types';
import React, { useMemo } from 'react';
import { useRoute } from '../../providers/RouteProvider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useDataFormPage } from '../../providers/DataFormPageProvider';

export interface IControlPanelLayoutProps {
	page: string;
	title?: string | React.ReactNode;
	text?: string | React.ReactNode;
	selectOnly?: boolean;
	selectSx?: SxProps;
	print?: boolean;
}
const ControlPanelLayout = ({
	page,
	title,
	text,
	selectOnly = false,
	selectSx,
	print
}: IControlPanelLayoutProps) => {
	const theme = useTheme();
	const {
		selectedState,
		setSelectedState,
		election,
		setElection,
		congressTab,
		setCongressTab,
		seat,
		setSeat,
		raceFilter,
		setRaceFilter,
		donorTab,
		setDonorTab
	} = useRoute();
	const { availableStates } = useDataFormPage();

	const isStateView = page === 'state';
	const isDonorState = page === 'donorState';
	const isDonorNational = page === 'donorNational';
	const baseOptions =
		isStateView || isDonorState
			? election === 'state'
				? TEN_STATE_STATE_OPTIONS
				: TEN_STATE_OPTIONS
			: isDonorState
			? TEN_STATE_OPTIONS
			: STATE_OPTIONS;

	// Filter options based on available states (for national view)
	const options = useMemo(() => {
		// Only apply filtering if we have a restricted list of available states
		if (availableStates && availableStates.length > 0) {
			return baseOptions.map((option) => ({
				...option,
				disabled: !availableStates.includes(option.id.toUpperCase())
			}));
		}
		// Otherwise, return all options as-is (no disabled states)
		return baseOptions;
	}, [availableStates, baseOptions]);
	const electionOptions =
		isStateView || isDonorState
			? election === 'congress'
				? !TEN_STATE_STATE_OPTIONS.map((state) => state.id).includes(
						selectedState ?? ''
						 
				  )
					? ELECTION_OPTIONS.filter((opt) => opt.id === 'congress')
					: ELECTION_OPTIONS
				: ELECTION_OPTIONS
			: isDonorState
			? [ELECTION_OPTIONS[1]]
			: ELECTION_OPTIONS;

	const isStateElection = election === 'state';
	const matches = useMediaQuery(theme.breakpoints.up('sm'));
	//console.log('congressTab', congressTab);
	const isState = isStateView && isStateElection;

	return (
		<>
			{!isDonorNational && (
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					sx={{
						backgroundColor: 'rgba(239,245,248,.5)',
						//theme.colorSchemes.light.palette.chartElementsBackground.bg,
						padding: { xs: '1rem 1rem', md: '1rem 3rem' },
						...selectSx
					}}
				>
					<Box sx={{ margin: matches ? '0 2rem 0 0' : '0 1rem .5rem 0' }}>
						<Select
							value={selectedState}
							options={options}
							onChange={setSelectedState}
							width={matches ? '200px' : '70px'}
							showId={matches ? false : true}
							print={print}
							label='Select a state'
						/>
					</Box>
					{(isStateView || isDonorState) && (
						<Box>
							<Select
								value={election}
								options={electionOptions}
								onChange={setElection}
								width={election === 'state' ? '200px' : '300px'}
								print={print}
								label='Select election type'
							/>
						</Box>
					)}
				</Stack>
			)}

			{!selectOnly && (
				<Box
					sx={{
						backgroundColor: theme.colorSchemes.light.palette.background.surface
					}}
				>
					<Section
						sx={{
							marginTop: '22px',
							maxWidth: 560,
							paddingLeft: { xs: '1rem', md: '3rem' }
						}}
						heading={title}
					>
						<Typography level='body1'>{text}</Typography>
					</Section>

					<Stack
						direction={{
							xs: page === 'state' && election === 'state' ? 'row' : 'column',
							md: 'row'
							//lg: isDonorNational ? 'column' : 'row'
						}}
						alignItems={{
							xs: 'flex-start',
							md: isDonorNational ? 'flex-start' : 'center'
							//lg: isDonorNational ? 'flex-start' : 'center'
						}}
						sx={{ margin: { xs: '2rem 1rem', md: '2rem 3rem' } }}
					>
						{!isStateElection || !isStateView ? (
							<Box
								sx={{
									margin: {
										xs: '0 1.5rem 1rem 0',
										md: isDonorNational ? '0 1.5rem 1rem 0' : '0 1.5rem 0 0',
										lg: isDonorNational ? '0 1.5rem 1rem 0' : '0 1.5rem 0 0'
									}
								}}
							>
								<Tabs
									value={
										isDonorState || isDonorNational ? donorTab : congressTab
									}
									onChange={
										isDonorState || isDonorNational
											? setDonorTab
											: setCongressTab
									}
									options={
										isDonorState || isDonorNational
											? DONOR_TABS
											: CONGRESSIONAL_TABS
									}
									width={isDonorState || isDonorNational ? '150px' : '120px'}
									print={print}
								></Tabs>
							</Box>
						) : null}
						{!isDonorState && (
							<Box>
								<RadioButtons
									values={seat}
									options={SEAT_OPTIONS}
									onChange={setSeat}
									print={print}
									page={page}
								/>
							</Box>
						)}
						{(isState || isDonorNational) && (
							<Box
								sx={{
									margin: {
										xs: isDonorNational ? '.4rem 0 0 0' : '.4rem 0 0 1.25rem',
										md: isDonorNational ? '.4rem 0 0 0' : '0 0 0 1.25rem',
										lg: isDonorNational ? '.4rem 0 0 0' : '0 0 0 1.25rem'
									}
								}}
							>
								<Checkbox
									values={raceFilter}
									options={isDonorNational ? DONOR_FILTER_BOX : FILTER_BOX}
									onChange={setRaceFilter}
									print={print}
								/>
							</Box>
						)}
					</Stack>
				</Box>
			)}
		</>
	);
};

export default ControlPanelLayout;
